import { Component, createRef } from 'react';
import classNames from 'classnames/bind';
import autoBind from 'react-autobind';

import TopicsSvc from '../../../services/TopicsSvc';

import { DIRECTORIES_PATH, TOPIC_CLASS_BREADCRUMBS } from '../../../data/directory/constants';
import { CHARTS } from '../../../data/directory/topic_classes/tabPaths';

import { getFormattedQuery } from '../../../helpers/helpers';
import { getTrendingBarAssetQuery } from '../../../helpers/watchlistHelperFunctions';
import { setSEOTopicMetadata } from '../../../helpers/metaDataHelpers';
import loadable from '../../../utils/loadable';

import Breadcrumbs from '../../Breadcrumbs';
import Preloader from '../../Preloader';

import Styles from './styles.module.scss';

const Charts = loadable(() => import('../Charts'));

const cx = classNames.bind(Styles);

class ChartsExpand extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      currentPath: '',
    };

    this.mounted = true;
    autoBind(this);
    this.chartsContainer = createRef();
  }

  async componentDidMount() {
    window.scrollTo(0, 0);
    await this.prepareData();
    window.addEventListener('resize', this.onResize, { passive: true });
  }

  componentWillUnmount() {
    this.mounted = false;
    window.removeEventListener('resize', this.onResize);
  }

  onResize() {
    this.forceUpdate();
  }

  onChartsInit() {
    this.setState({ loading: false });
  }

  async getAssetData() {
    try {
      const { isWatchlist, topicSlug, assetType } = this.props;

      const topic = await TopicsSvc.getTopicBySlug(topicSlug);

      if (!topic) {
        window.location.href = '/404';
        return;
      }

      const assetClass = topic.asset_class;

      if (!window.location.pathname.includes(`${DIRECTORIES_PATH}/${assetClass.slug}`)) {
        window.location.href = '/404';
        return;
      }

      if (!(assetClass instanceof Object && topic instanceof Object)) {
        window.location.pathname = '/404';
        return;
      }

      const breadcrumbs = TOPIC_CLASS_BREADCRUMBS.concat([
        {
          name: assetClass.title,
          path: assetClass.slug,
        },
        {
          name: topic.name,
          path: `${assetClass.slug}/${topicSlug}`,
        },
        {
          name: 'Charts',
          path: `${assetClass.slug}/${topicSlug}/charts`,
        },
      ]);

      this.setState({
        breadcrumbs,
        currentPath: `${DIRECTORIES_PATH}/${assetClass.slug}/${topicSlug}/charts`,
      });

      if (!isWatchlist) {
        const ticker = topic?.tickers?.find((f) => f.default)?.name;
        setSEOTopicMetadata({
          activeTab: CHARTS,
          topicName: topic.name,
          topicType: assetType,
          figis: topic.figis,
          topicTicker: (ticker || '').toUpperCase(),
        });
      }

      return {
        assetClass,
        topic,
      };
    } catch (e) {
      console.log(e);
      return {};
    }
  }

  async getAssetTabData(topic) {
    const availableTickersArr = topic?.tickers.filter((ticker) => ticker.default);

    // eslint-disable-next-line no-param-reassign
    topic.mainTicker = availableTickersArr[0]?.full_name;

    return {
      topic,
    };
  }

  async prepareData() {
    const { isWatchlist, topic: propsTopic } = this.props;
    if (isWatchlist) {
      const newState = await this.getAssetTabData(propsTopic);
      const state = {
        ...newState,
        assetClass: propsTopic.asset_class,
        currentPath: '/watchlists/charts',
      };
      return new Promise((resolve) => {
        this.setState({ ...state }, resolve);
      });
    }

    const {
      assetClass,
      topic,
    } = (await this.getAssetData()) || {};

    if (this.mounted) {
      const state = {
        assetClass,
        topic,
      };
      const newState = await this.getAssetTabData(topic);
      return new Promise((resolve) => {
        this.setState({ ...state, ...newState }, resolve);
      });
    }
  }

  render() {
    const {
      loading, breadcrumbs, topic, currentPath,
    } = this.state;
    const { isWatchlist, assetType, onChartCollapseClick } = this.props;

    const { mainTicker } = topic || {};

    return (
      <div className={Styles.charts_expand}>
        {!isWatchlist && <Breadcrumbs items={breadcrumbs} />}
        <Preloader loading={loading} static centered />
        <div className={cx('charts-and-data')} ref={this.chartsContainer}>
          {topic && (mainTicker || topic.trading_view_ticker) && (
            <div className={cx('chart-holder', { invisible: loading })}>
              <Charts
                isExpanded
                hasBigScreenDesign
                currentPath={currentPath}
                assetType={assetType}
                selector={[mainTicker]}
                width={(
                  (this.chartsContainer.current && this.chartsContainer.current.clientWidth)
                  || 0
                )}
                onDataInit={this.onChartsInit}
                query={(
                  getFormattedQuery(
                    getTrendingBarAssetQuery(topic, 'Topic'),
                  )
                )}
                slug={topic}
                onChartCollapseClick={onChartCollapseClick}
              />
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default ChartsExpand;
